import { createContext, useContext, useMemo, type ReactNode } from 'react';
import { useRouter, useSearch } from '@tanstack/react-router';
import {
  isCustomPreset,
  normalizePreset,
  normalizeView,
  type AnalyticsPreset,
  type AnalyticsView,
} from './analyticsRange.js';

/**
 * AnalyticsControls — the shared date + view + organizational-filter state for
 * the analytics workspace. It is URL-as-truth: every control writes to
 * `/dashboard` search params, so a selected period or program/site filter
 * persists across view switches and deep links. There is no local state to
 * drift out of sync.
 *
 * Organizational filters (programs+sites follow-up): `programId` applies to
 * every analytics view; `siteId` is only meaningful for schedule/shift data and
 * is only offered (and sent) from the Schedule view.
 */

export interface AnalyticsPeriodQuery {
  preset: AnalyticsPreset;
  from?: string;
  to?: string;
}

export interface AnalyticsControls {
  view: AnalyticsView;
  preset: AnalyticsPreset;
  from: string;
  to: string;
  /** Selected program filter ('' = all). */
  programId: string;
  /** Selected site filter ('' = all); only used by the Schedule view. */
  siteId: string;
  setView: (view: AnalyticsView) => void;
  setPreset: (preset: AnalyticsPreset) => void;
  setCustomRange: (from: string, to: string) => void;
  setProgram: (programId: string) => void;
  setSite: (siteId: string) => void;
  /** Ready-to-use query object for the analytics API endpoints. */
  periodQuery: AnalyticsPeriodQuery;
  /** Program/site filter params for the analytics API endpoints. */
  filterQuery: { programId?: string; siteId?: string };
  /** Period + filters merged — the single object most endpoints take. */
  apiQuery: AnalyticsPeriodQuery & { programId?: string; siteId?: string };
}

const AnalyticsContext = createContext<AnalyticsControls | null>(null);

export function AnalyticsProvider({ children }: { children: ReactNode }) {
  const router = useRouter();
  const search = useSearch({ strict: false }) as {
    view?: string;
    preset?: string;
    from?: string;
    to?: string;
    program?: string;
    site?: string;
  };

  const view = normalizeView(search.view);
  const preset = normalizePreset(search.preset);
  const from = search.from ?? '';
  const to = search.to ?? '';
  const programId = search.program ?? '';
  const siteId = search.site ?? '';

  const value = useMemo<AnalyticsControls>(() => {
    const navigate = (patch: Record<string, string | undefined>) => {
      const merged: Record<string, string | undefined> = {
        view: search.view,
        preset: search.preset,
        from: search.from,
        to: search.to,
        program: search.program,
        site: search.site,
        ...patch,
      };
      const next: Record<string, string> = {};
      for (const [key, v] of Object.entries(merged)) {
        if (v) next[key] = v;
      }
      void router.navigate({ to: '/dashboard', search: next });
    };

    const periodQuery: AnalyticsPeriodQuery = {
      preset,
      ...(isCustomPreset(preset) ? { from: from || undefined, to: to || undefined } : {}),
    };
    const filterQuery = {
      ...(programId ? { programId } : {}),
      ...(siteId ? { siteId } : {}),
    };

    return {
      view,
      preset,
      from,
      to,
      programId,
      siteId,
      setView: (next) => navigate({ view: next === 'pulse' ? undefined : next }),
      setPreset: (next) =>
        navigate(
          next === 'custom' ? { preset: next } : { preset: next, from: undefined, to: undefined },
        ),
      setCustomRange: (nextFrom, nextTo) =>
        navigate({ preset: 'custom', from: nextFrom || undefined, to: nextTo || undefined }),
      setProgram: (next) => navigate({ program: next || undefined }),
      setSite: (next) => navigate({ site: next || undefined }),
      periodQuery,
      filterQuery,
      apiQuery: { ...periodQuery, ...filterQuery },
    };
  }, [
    router,
    search.view,
    search.preset,
    search.from,
    search.to,
    search.program,
    search.site,
    view,
    preset,
    from,
    to,
    programId,
    siteId,
  ]);

  return <AnalyticsContext.Provider value={value}>{children}</AnalyticsContext.Provider>;
}

export function useAnalyticsControls(): AnalyticsControls {
  const ctx = useContext(AnalyticsContext);
  if (!ctx) throw new Error('useAnalyticsControls must be used inside <AnalyticsProvider>.');
  return ctx;
}
